import React, { useEffect, useState } from "react";
import WaitTimesTable from "./WaitTimesTable";
import { useParams } from "react-router-dom";
import { getLocationInfo } from "./utils/api";
import {
  Box,
  Flex,
  Heading,
  Text,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  Divider,
  Spinner,
  Button,
  Link,
  List,
  ListItem,
  Image,
  SimpleGrid,
} from "@chakra-ui/react";
import { rootDomain } from "./utils/domainUtils";
import { programTypeToName, programTypeToUrlString } from "./utils/programs";
import { CheckCircleIcon } from "@chakra-ui/icons";
import ReactTimeAgo from "react-time-ago";
import moment from "moment";
import chakraTheme from "./constants/chakraTheme";
import { Helmet } from "react-helmet";

const PAGE_STATES = {
  LOADING: "LOADING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

const WaitTime = ({ waitTimeDays }) => {
  let color, text;

  if (waitTimeDays > 365 / 2) {
    color = chakraTheme.colors.red[200];
    if (waitTimeDays >= 365) {
      text = "1 year";
    } else {
      text = `${waitTimeDays} days`;
    }
  } else if (waitTimeDays > 30) {
    color = chakraTheme.colors.yellow[200];
    text = `${waitTimeDays} days`;
  } else {
    color = chakraTheme.colors.green[100];
    if (waitTimeDays === 1) {
      text = `${waitTimeDays} day`;
    } else {
      text = `${waitTimeDays} days`;
    }
  }

  return (
    <Box>
      <Flex
        bg={color}
        p="20px"
        justifyContent="space-between"
        alignItems="center"
        borderRadius="lg"
      >
        <strong>Current interview wait time: {text}</strong>
        {waitTimeDays > 30 ? (
          <Button
            as={Link}
            href="https://appointmentscanner.com?ref=waittimes-info-page-top"
          >
            Find a sooner interview
          </Button>
        ) : (
          <Button as={Link} href="https://ttp.dhs.gov">
            Visit booking page
          </Button>
        )}
      </Flex>
    </Box>
  );
};

const EnrollmentCenterInfoPage = ({ locationId, programType }) => {
  const { locationString } = useParams();
  const [locationData, setLocationData] = useState({});
  const [pageState, setPageState] = useState(PAGE_STATES.LOADING);

  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const fetchAndSetLocationInfo = async () => {
      try {
        const data = await getLocationInfo(programType, locationString);
        setLocationData(data);
        setUpdatedAt(data.updatedAt);
        setPageState(PAGE_STATES.SUCCESS);
      } catch (err) {}
    };
    fetchAndSetLocationInfo();
  }, [programType]);

  const fullAddress = `${locationData.address} ${locationData.addressAdditional}, ${locationData.city}, ${locationData.state} ${locationData.postalCode}`;

  if (pageState === PAGE_STATES.SUCCESS) {
    return (
      <>
        <Helmet>
          <title>
            {locationData.name} | {programTypeToName[programType]} Interview
            Guide
          </title>
        </Helmet>
        <Flex
          bg="white"
          minH="100vh"
          justifyContent="center"
          pb="50px"
          px={{ base: "10px", md: 0 }}
        >
          <Box w={{ base: "100vw", md: "800px" }}>
            <Breadcrumb my="20px">
              <BreadcrumbItem>
                <BreadcrumbLink href={rootDomain}>Programs</BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem>
                <BreadcrumbLink
                  href={`${rootDomain}/${programTypeToUrlString[programType]}`}
                >
                  {programTypeToName[programType]} Locations
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink>{locationData.name}</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
            <Box textAlign="center" mb="50px">
              <Heading as="h1">{locationData.name}</Heading>
              <Text as="h2">
                {locationData.city}, {locationData.state},{" "}
                {locationData.countryCode}
              </Text>
              <Heading as="h2" size="md" mt="20px">
                {programTypeToName[programType]} Interview Guide (Unofficial)
              </Heading>
              <br />
              <Text as="h2">
                How to get a {programTypeToName[programType]} interview at{" "}
                {locationData.name}—even when there are no appointments
                available.
              </Text>
            </Box>

            <WaitTime waitTimeDays={locationData.waitTimeDays} />

            {updatedAt && (
              <Flex p="10px" pt="10px" alignItems="center">
                <CheckCircleIcon size="sm" color="green.500" mr="10px" />
                <Text mr="5px">Updated</Text>
                <ReactTimeAgo
                  date={moment(updatedAt).toDate()}
                  locale="en-US"
                />
              </Flex>
            )}

            {!updatedAt && (
              <Flex p="10px" pt="10px" alignItems="center">
                <Spinner size="xs" color="blue.500" mr="10px" />
                {/* <CheckCircleIcon size="sm" color="blue.500" mr="10px" /> */}
                <Text mr="5px">Updating</Text>
              </Flex>
            )}
            <br />

            <Divider />
            <Heading as="h2" size="lg" mt="50px" mb="20px">
              Booking a {programTypeToName[programType]} interview at{" "}
              {locationData.name}
            </Heading>

            <List>
              <ListItem>
                <Heading as="h3" size="md" mt="50px" mb="20px">
                  1. Submit your {programTypeToName[programType]} application
                  online
                </Heading>
                <Text>
                  You can apply{" "}
                  <Link href="https://ttp.dhs.gov/" textDecor="underline">
                    on the DHS Trusted Traveler Program website.
                  </Link>{" "}
                  You'll need to pay the application fee and fill out all the
                  information about your background.
                </Text>
              </ListItem>
              <ListItem>
                <Heading as="h3" size="md" mt="50px" mb="20px">
                  2. Wait for conditional approval
                </Heading>
                <Text>
                  Getting conditional approval for your{" "}
                  {programTypeToName[programType]} can take anywhere from 24
                  hours to a few months depending on your specific circumstances
                  and the current backlog of applications.
                </Text>
                <br />
                <Text>
                  Check back on your application often. The DHS may also notify
                  you by email once your application is approved, if you gave
                  them permission to contact you.
                </Text>
              </ListItem>
              <ListItem>
                <Heading as="h3" size="md" mt="50px" mb="20px">
                  3. Schedule your interview (even with "no appointments
                  available for this location")
                </Heading>
                <Text>
                  Now, the hardest part–scheduling your{" "}
                  {programTypeToName[programType]} interview.
                </Text>
                <br />
                <Flex justifyContent="center">
                  <Button
                    as={Link}
                    backgroundColor="#202B4A"
                    color="white"
                    _hover={{
                      backgroundColor: "#283354",
                      textDecoration: "none",
                    }}
                    _active={{ backgroundColor: "#283354" }}
                    variant="solid"
                    href="https://appointmentscanner.com?ref=waittimes-info-page-middle"
                  >
                    Sign up for {programTypeToName[programType]} interview
                    alerts
                  </Button>
                </Flex>
                <br />
                <Text>
                  Enrollment centers across the country are struggling to keep
                  up with the backlog of {programTypeToName[programType]}{" "}
                  applicants. It's common to run into one of two scenarios:
                </Text>
                <br />
                <SimpleGrid
                  w="100%"
                  columns={{ base: 1, md: 2 }}
                  spacingY={{ base: 10, md: 0 }}
                  justifyContent="center"
                  alignItems="start"
                >
                  <Flex flexDir="column" justifyContent="center">
                    <Heading as="h4" size="sm" mb="20px">
                      The soonest appointment is months or <i>years</i> away
                    </Heading>
                    <Image
                      src="/locations/far-away.jpg"
                      width="300px"
                      boxShadow="lg"
                    />
                  </Flex>
                  <Flex flexDir="column" justifyContent="center">
                    <Heading as="h4" size="sm" mb="20px">
                      "No appointments available for this location" message
                    </Heading>
                    <Image
                      src="/locations/no-appointments.jpg"
                      width="300px"
                      boxShadow="lg"
                    />
                  </Flex>
                </SimpleGrid>
                <br />
                <br />
                <Text>
                  {" "}
                  The secret: in <i>both</i> cases, new appointments (usually
                  1-60 days out) will become available every single day when
                  other applicants cancel or reschedule their appointments.
                </Text>
                <br />
                <Text>
                  <i>
                    <strong>Tip:</strong> The confusing "No appointments
                    available for this location" message just means that all the
                    interview appointments are currently booked. It does{" "}
                    <strong>
                      <i>not</i>
                    </strong>{" "}
                    mean the center is closed or not conducting interviews.
                  </i>
                </Text>
                <br />
                <br />
                <Heading as="h4" size="sm">
                  Finding a sooner {programTypeToName[programType]} interview
                  appointment
                </Heading>
                <br />
                <Text>
                  Tons of last minute interview appointments become available at
                  nearly every enrollment center, every single day. The catch?
                  These new interview appointments get taken <i>fast</i>.
                  They'll often be snatched up within minutes after they appear,
                  so you need to be quick!
                </Text>
                <br />
                <Text>
                  One option is to try your luck at refreshing the scheduler
                  page periodically thoughout the day and seen if any
                  appointments show up. Don't want to sit at your computer,
                  refreshing all day? A better strategy is to use Appointment
                  Scanner to get text message alerts when interview appointments
                  open up:
                </Text>
                <Heading as="h4" size="sm" mt="50px">
                  Using Appointment Scanner to get alerts for{" "}
                  {programTypeToName[programType]} interview appointments
                </Heading>
                <br />
                <Text>
                  The most reliable way to book a{" "}
                  {programTypeToName[programType]} interview when you can't find
                  on on your own is to sign up for interview alerts from
                  Appointment Scanner. Appointment Scanner monitors 24/7 for
                  interview appointments at your preferrred enrollment centers
                  and sends you text messages or emails to alert you when
                  appointments open up. When you get an alert, log into your{" "}
                  {programTypeToName[programType]} account and try to schedule
                  the appointment as fast as you can, just like you normally
                  would.
                </Text>
                <br />
                <Text>
                  Appointment Scanner works for all of the{" "}
                  {programTypeToName[programType]} enrollment centers, even when
                  they say "no appointments available for this location."
                </Text>
                <br />
                <Text>
                  <i>
                    <strong>Tip:</strong> You can also customize your
                    appointment date preferences in Appointment Scanner, so that
                    you're only alerted about appointments that fit your
                    schedule.
                  </i>
                </Text>
                <br />
                <Text>
                  Appointment Scanner has helped thousands of applicants book
                  their interview appointments and save months or years of
                  waiting.
                </Text>
                <br />
                <Flex justifyContent="center">
                  <Button
                    as={Link}
                    backgroundColor="#202B4A"
                    color="white"
                    _hover={{
                      backgroundColor: "#283354",
                      textDecoration: "none",
                    }}
                    _active={{ backgroundColor: "#283354" }}
                    variant="solid"
                    href="https://appointmentscanner.com?ref=waittimes-info-page-bottom"
                  >
                    Sign up for {programTypeToName[programType]} interview
                    alerts
                  </Button>
                </Flex>
                <br />
                <br />
                <Text fontSize="xs">
                  <i>
                    Appointment Scanner is not affiliated with or endorsed by
                    any government agencies or programs. It's a privately-owned
                    service that helps users find interview appointments.
                  </i>
                </Text>
                {/* <Heading as="h4" size="sm" mt="50px" mb="20px">
                  Refresh the interview schedule page all day
                </Heading> */}
                {/* <Heading as="h4" size="sm" mt="50px" mb="20px">
                  Schedule an international trip and use the "Enrollment on
                  Arrival" program
                </Heading>
                <br /> */}
              </ListItem>
              {/* <ListItem>
                <Heading as="h2" size="md" mt="50px" mb="20px">
                  4. Receive your card in the mail
                </Heading>
                <Text>
                  If all goes well at the interview, you'll receive your card in
                  the mail, and you're all done!
                </Text>
              </ListItem> */}
            </List>
            <Divider mt="50px" />

            <Heading as="h2" mt="50px" mb="20px" size="lg">
              {locationData.name}
            </Heading>
            <Flex
              w={{ base: "100%", md: "100%" }}
              // minH={{ base: "500px", md: "500px" }}
              borderRadius="xl"
              boxShadow="lg"
              borderColor="gray.200"
              borderWidth="1px"
              justifyContent="start"
              alignItems="center"
              direction="column"
            >
              <iframe
                width="100%"
                height="300px"
                style={{
                  border: 0,
                  borderTopRightRadius: "10px",
                  borderTopLeftRadius: "10px",
                }}
                title="test"
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/place?q=${fullAddress}&key=AIzaSyAQXwqBS95csf08_eqHySZJ3sJe6iM4_Kk`}
              />
              <Box textAlign="left" width="100%" p="50px">
                <Text>{locationData.address}</Text>
                <Text>{locationData.addressAdditional}</Text>
                <Text>
                  {locationData.city}, {locationData.state}{" "}
                  {locationData.postalCode}
                </Text>
                {locationData.countryCode === "US" && <Text>USA</Text>}
                {locationData.countryCode === "CA" && <Text>Canada</Text>}
                <br />
                <Text>
                  Phone: +{locationData.phoneCountryCode}{" "}
                  {locationData.phoneNumber} {locationData.phoneExtension}
                </Text>
                {locationData.phoneAltNumber && (
                  <Text>
                    Alternate phone: +{locationData.phoneAltCountryCode}{" "}
                    {locationData.phoneAltNumber}{" "}
                    {locationData.phoneAltExtension}
                  </Text>
                )}
                <br />

                {/* <Heading size="sm" as="h4">
                  Directions
                </Heading>
                <Text>{locationData.directions}</Text>
                <br />

                <Heading size="sm" as="h4">
                  Notes
                </Heading>
                <Text>{locationData.notes}</Text> */}
              </Box>
            </Flex>
          </Box>
        </Flex>
      </>
    );
  } else {
    return <Flex bg="white" minH="100vh" justifyContent="center"></Flex>;
  }
};

export default EnrollmentCenterInfoPage;
